import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from './ui/sheet';
import { Plus, Trash2, UserPlus, Search } from 'lucide-react';
import { toast } from 'sonner';
import QuickAddCustomerDialog from './QuickAddCustomerDialog';
import MembershipPlanPicker from './MembershipPlanPicker';
import DraftStatus from './DraftStatus';
import { useDurableDraft } from '../hooks/useDurableDraft';
import { JobContextPanel } from './JobContextPanel';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const makeKey = () => (typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : `li_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`);
const withKey = (li) => (li && li._key ? li : { ...li, _key: makeKey() });

const initialForm = () => ({
  customer_id: '',
  // iter150 — Default each new line to kind=service so the membership discount
  // math runs correctly out of the box. Tech can switch to membership/credit/fee/tax.
  line_items: [{ _key: makeKey(), description: '', quantity: 1, unit_price: 0, kind: 'service', warranty_covered: false }],
  notes: '',
  status: 'pending',
  deposit_amount: 0,
});

// iter150 — Allowed line kinds. Order = display order in the dropdown.
const LINE_KIND_OPTIONS = [
  { value: 'service',    label: 'Service',     hint: '10% member discount eligible' },
  { value: 'membership', label: 'Membership',  hint: 'Activates membership on paid' },
  { value: 'credit',     label: 'Credit',      hint: 'Negative / promo line' },
  { value: 'fee',        label: 'Fee',         hint: 'Permits / trip / financing' },
  { value: 'tax',        label: 'Tax',         hint: 'Never discounted' },
];

// iter233 — tier code → short label for the catalog line badge.
const CATALOG_TIER_LABEL = { hsp: 'HSP', residential: 'Residential', property_management: 'Property Mgmt', commercial: 'Commercial' };

/**
 * Shared "Create Invoice/Estimate" sheet. Pass type='invoice' or 'estimate'.
 */
const BillingCreateSheet = ({ type = 'invoice', onCreated, trigger = true, open: openProp, onOpenChange: onOpenChangeProp, defaultCustomerId: defaultCustomerIdProp, jobContext }) => {
  const defaultCustomerId = jobContext?.customer_id || defaultCustomerIdProp;
  const [credit, setCredit] = useState({ open: false, techId: '', reason: '' });
  const [additional, setAdditional] = useState({ on: false, reason: '' });
  const [conflict, setConflict] = useState(null);
  const isInvoice = type === 'invoice';
  const [openInternal, setOpenInternal] = useState(false);
  const open = openProp !== undefined ? openProp : openInternal;
  const setOpen = onOpenChangeProp || setOpenInternal;
  const [customers, setCustomers] = useState([]);
  const [pricebook, setPricebook] = useState([]);   // iter233 — Service Catalog items (tier-resolved)
  const [showMembershipPicker, setShowMembershipPicker] = useState(false);  // iter298
  const [tier, setTier] = useState('residential');
  const [form, setForm] = useState({ ...initialForm(), customer_id: defaultCustomerId || '' });
  const [loading, setLoading] = useState(false);
  const [customerQuery, setCustomerQuery] = useState('');
  const [quickAddOpen, setQuickAddOpen] = useState(false);

  // Durable draft — survives app switching, reloads, auth/clock refresh and lost signal.
  // Cleared only after a successful create or an explicit Discard Draft.
  const draft = useDurableDraft({
    scopeKey: `billing_create:${type}${jobContext ? `:job:${jobContext.appointment_id}` : (defaultCustomerId ? `:${defaultCustomerId}` : '')}`,
    value: { ...form, line_items: form.line_items.map(({ _key, ...li }) => li), tier },
    onRestore: (d) => {
      const { tier: dTier, ...rest } = d || {};
      setForm({ ...initialForm(), ...rest, line_items: (rest.line_items || []).length ? rest.line_items.map(withKey) : initialForm().line_items });
      if (dTier) setTier(dTier);
      if (openProp === undefined) setOpenInternal(true);
    },
  });
  const discardDraft = async () => {
    if (!window.confirm(`Discard this unfinished ${isInvoice ? 'invoice' : 'estimate'}?`)) return;
    await draft.clear();
    setTier('residential');
    setForm({ ...initialForm(), customer_id: defaultCustomerId || '' });
    toast.success('Draft discarded');
  };

  useEffect(() => {
    if (!open) return;
    // Never reset on open — an in-progress draft must survive close/reopen.
    if (defaultCustomerId) setForm((f) => (f.customer_id ? f : { ...f, customer_id: defaultCustomerId }));
    if (jobContext) setForm((f) => ({ ...f, customer_id: jobContext.customer_id, notes: f.notes || jobContext.work_performed || '' }));
    setCustomerQuery('');
    axios.get(`${API}/customers`, { withCredentials: true }).then((r) => r.data).catch(() => [])
      .then((cs) => setCustomers((cs || []).filter((c) => !c.merged_into)));
  }, [open, defaultCustomerId, jobContext]);

  // iter233 — Load the canonical Service Catalog resolved for the selected tier.
  useEffect(() => {
    if (!open) return;
    axios.get(`${API}/service-catalog/lookup?tier=${tier}`, { withCredentials: true })
      .then((r) => r.data?.items || []).catch(() => [])
      .then((items) => setPricebook(items));
  }, [open, tier]);

  const filteredCustomers = useMemo(() => {
    const q = customerQuery.trim().toLowerCase();
    const list = !q ? customers : customers.filter((c) =>
      (c.name || '').toLowerCase().includes(q) ||
      (c.email || '').toLowerCase().includes(q) ||
      (c.phone || '').toLowerCase().includes(q)
    );
    return list.slice(0, 50);
  }, [customers, customerQuery]);

  const selectedCustomer = customers.find((c) => c.id === form.customer_id);

  // iter233 — Derive the tier from the selected customer's billing context (visible,
  // user-overridable). HSP/warranty → hsp; else the customer's stored type.
  useEffect(() => {
    if (!selectedCustomer) return;
    const ct = (selectedCustomer.customer_type || '').toLowerCase();
    let derived = null;
    if (ct === 'warranty_company' || ct === 'hsp' || selectedCustomer.warranty_payor_id) derived = 'hsp';
    else if (ct === 'property_management') derived = 'property_management';
    else if (ct === 'commercial') derived = 'commercial';
    else if (ct === 'residential') derived = 'residential';
    if (derived) setTier(derived);
  }, [selectedCustomer]);

  const computeLineTotal = (li) => Number(li.quantity || 0) * Number(li.unit_price || 0);
  const subtotal = form.line_items.reduce((s, li) => s + computeLineTotal(li), 0);

  const updateLine = (idx, field, value) => {
    const next = [...form.line_items];
    next[idx] = { ...next[idx], [field]: value };
    setForm({ ...form, line_items: next });
  };
  const addLine = () => setForm({ ...form, line_items: [...form.line_items, { _key: makeKey(), description: '', quantity: 1, unit_price: 0, kind: 'service', warranty_covered: false }] });
  const removeLine = (idx) => setForm({ ...form, line_items: form.line_items.filter((_, i) => i !== idx) });

  const addFromCatalog = (idx, sku) => {
    const item = pricebook.find((p) => p.sku === sku);
    if (!item) return;
    const r = item.resolved || {};
    // iter233 — Not Available for this tier → block (never create a $0 line).
    if (r.pricing_method === 'not_available') {
      toast.error(`${item.name} is Not Available for the ${r.tier_label} tier.`);
      return;
    }
    const snapDesc = item.name + (item.customer_scope ? ` — ${item.customer_scope}` : '');
    const next = [...form.line_items];
    const est = r.pricing_method === 'estimate_required';
    next[idx] = {
      ...next[idx],
      description: snapDesc,
      unit_price: est ? 0 : (r.price ?? 0),
      kind: 'service',
      // Snapshot + resolution metadata (immutable on the saved line).
      catalog_service_id: item.id,
      sku: item.sku,
      snapshot_name: item.name,
      snapshot_description: item.customer_scope || '',
      resolved_tier: r.tier,
      tier_resolution_source: selectedCustomer ? 'customer_type' : 'manual',
      pricing_method: r.pricing_method,
      membership_eligible: item.membership_eligible !== false,
      estimate_required: est,
      _catalog: true,
    };
    setForm({ ...form, line_items: next });
    if (est) toast.info(`${item.name} is Estimate Required — enter an approved custom price or create an estimate (not $0).`);
  };

  const onCustomerCreated = (cust) => {
    if (!cust) return;
    setCustomers((prev) => {
      if (prev.find((c) => c.id === cust.id)) return prev;
      return [{ id: cust.id, name: cust.name, email: cust.email || '', phone: cust.phone || '' }, ...prev];
    });
    setForm((prev) => ({ ...prev, customer_id: cust.id }));
    setCustomerQuery('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.customer_id) { toast.error('Select a customer'); return; }
    const cleanLines = form.line_items.filter((li) => li.description.trim() || Number(li.unit_price) > 0);
    if (cleanLines.length === 0) { toast.error('Add at least one line item'); return; }
    setLoading(true);
    try {
      const url = isInvoice ? `${API}/invoices` : `${API}/estimates`;
      const payload = {
        customer_id: form.customer_id,
        line_items: cleanLines.map((li) => ({
          description: li.description,
          quantity: Number(li.quantity) || 0,
          unit_price: Number(li.unit_price) || 0,
          // iter150 — carry the categorization through so the backend math knows
          // which lines are membership-discount-eligible vs membership sale vs credit.
          kind: li.kind || 'service',
          warranty_covered: !!li.warranty_covered,
          // iter298 — carry structured membership plan identity for plan-aware activation.
          ...(li.membership_plan_key ? {
            membership_plan_key: li.membership_plan_key,
            plan_name: li.plan_name,
            billing_cadence: li.billing_cadence,
            complimentary: !!li.complimentary,
          } : {}),
          // iter233 — Service Catalog snapshot + tier resolution (immutable on the line).
          ...(li.catalog_service_id ? {
            catalog_service_id: li.catalog_service_id,
            sku: li.sku,
            snapshot_name: li.snapshot_name,
            snapshot_description: li.snapshot_description,
            resolved_tier: li.resolved_tier,
            tier_resolution_source: li.tier_resolution_source,
            pricing_method: li.pricing_method,
            membership_eligible: li.membership_eligible,
            estimate_required: !!li.estimate_required,
          } : {}),
        })),
        notes: form.notes,
        deposit_amount: Number(form.deposit_amount) || 0,
        ...(isInvoice ? { status: form.status } : {}),
      };
      // iter95 — capture the response so the caller can immediately open the new doc
      // and surface staff actions (Send Email, Copy Payment Link, QR, Edit, Mark Paid).
      let newDoc;
      if (isInvoice && jobContext) {
        if (credit.open && credit.techId && credit.reason.trim().length < 3) { toast.error('Give a reason for changing the credited tech'); setLoading(false); return; }
        const extra = { additional_invoice: additional.on, additional_reason: additional.reason };
        try {
          if (jobContext.completion_id && !additional.on) {
            const { data } = await axios.post(`${API}/jobs/completion/${jobContext.completion_id}/invoice`, {
              line_items: payload.line_items.map((li) => ({ ...li, kind: li.kind === 'membership' ? 'membership' : 'service' })),
              notes: form.notes, office_followup: true, save_as_draft: false, ...extra,
            }, { withCredentials: true });
            newDoc = data;
          } else {
            // No completion form, or an explicit Additional Invoice: canonical create with the Job-level guard.
            ({ data: newDoc } = await axios.post(`${API}/invoices`, { ...payload, source_appointment_id: jobContext.appointment_id, ...extra }, { withCredentials: true }));
          }
        } catch (err) {
          const d = err.response?.data?.detail;
          if (err.response?.status === 409 && d?.code === 'job_already_invoiced') { setConflict(d); toast.error(d.message); setLoading(false); return; }
          throw err;
        }
        if (credit.open && credit.techId && newDoc?.id) {
          await axios.post(`${API}/admin/invoices/${newDoc.id}/assign-tech`, { technician_id: credit.techId, reason: credit.reason.trim(),
            linked_appointment_id: jobContext.appointment_id, linked_completion_id: jobContext.completion_id || null }, { withCredentials: true });
        }
      } else {
        ({ data: newDoc } = await axios.post(url, payload, { withCredentials: true }));
      }
      if (!jobContext) toast.success(`${isInvoice ? 'Invoice' : 'Estimate'} created — opening for next actions…`);
      await draft.clear();
      setTier('residential');
      setForm({ ...initialForm(), customer_id: defaultCustomerId || "" });
      setOpen(false);
      onCreated?.(newDoc);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
    setLoading(false);
  };

  return (
    <>
      <Sheet open={open} onOpenChange={setOpen}>
        {trigger && (
          <Button
            data-testid={`create-${type}-btn`}
            onClick={() => setOpen(true)}
            className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB]"
          >
            <Plus className="w-4 h-4 mr-1" /> New {isInvoice ? 'Invoice' : 'Estimate'}
          </Button>
        )}
        <SheetContent side="right" className="bg-[#0D0D0D] border-[#262626] text-white w-full sm:max-w-2xl overflow-y-auto p-4 sm:p-6">
          <SheetHeader>
            <SheetTitle className="text-[#D4AF37]">Create {isInvoice ? 'Invoice' : 'Estimate'}</SheetTitle>
          </SheetHeader>
          <DraftStatus status={draft.status} restoredAt={draft.restoredAt} onDiscard={discardDraft} testIdPrefix={`create-${type}-draft`} />
          <form onSubmit={handleSubmit} className="mt-5 space-y-4">
            {jobContext && <JobContextPanel ctx={jobContext} credit={credit} setCredit={setCredit} additional={additional} setAdditional={setAdditional} conflict={conflict} />}
            {/* Customer picker */}
            <div className={jobContext ? 'hidden' : ''}>
              <div className="flex items-center justify-between mb-1">
                <Label className="text-white">Customer</Label>
                <Button
                  data-testid={`create-${type}-quick-add-customer`}
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => setQuickAddOpen(true)}
                  className="border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-[#0A0A0A] h-8"
                >
                  <UserPlus className="w-3.5 h-3.5 mr-1" /> Quick Add
                </Button>
              </div>
              {selectedCustomer ? (
                <div data-testid={`create-${type}-selected-customer`} className="bg-[#0A0A0A] border border-[#D4AF37]/50 rounded-md p-3 flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-white font-medium truncate">{selectedCustomer.name}</p>
                    <p className="text-[#A3A3A3] text-xs truncate">{selectedCustomer.email || 'no email'} · {selectedCustomer.phone || 'no phone'}</p>
                  </div>
                  <Button data-testid={`create-${type}-change-customer`} type="button" size="sm" variant="outline" className="border-[#262626] text-white hover:bg-[#262626] h-8 shrink-0" onClick={() => setForm({ ...form, customer_id: '' })}>Change</Button>
                </div>
              ) : (
                <>
                  <div className="relative mb-2">
                    <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-[#525252]" />
                    <Input
                      data-testid={`create-${type}-customer-search`}
                      value={customerQuery}
                      onChange={(e) => setCustomerQuery(e.target.value)}
                      placeholder="Search by name, email, or phone…"
                      className="pl-8 bg-[#0A0A0A] border-[#262626] text-white h-11 text-base"
                    />
                  </div>
                  <div className="bg-[#0A0A0A] border border-[#262626] rounded-md max-h-56 overflow-y-auto divide-y divide-[#1f1f1f]">
                    {filteredCustomers.length === 0 ? (
                      <button
                        type="button"
                        data-testid={`create-${type}-no-customer`}
                        onClick={() => setQuickAddOpen(true)}
                        className="w-full text-left p-3 text-[#A3A3A3] hover:bg-[#1a1a1a] text-sm"
                      >
                        No customers match. <span className="text-[#D4AF37]">Click here to Quick Add →</span>
                      </button>
                    ) : (
                      filteredCustomers.map((c) => (
                        <button
                          key={c.id}
                          type="button"
                          data-testid={`create-${type}-customer-option-${c.id}`}
                          onClick={() => setForm({ ...form, customer_id: c.id })}
                          className="w-full text-left p-3 hover:bg-[#1a1a1a] text-sm"
                        >
                          <p className="text-white font-medium">{c.name}</p>
                          <p className="text-[#A3A3A3] text-xs">{c.email || 'no email'} · {c.phone || 'no phone'}</p>
                        </button>
                      ))
                    )}
                  </div>
                </>
              )}
            </div>

            {isInvoice && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-white">Status</Label>
                  <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                    <SelectTrigger data-testid={`create-${type}-status`} className="bg-[#0A0A0A] border-[#262626] text-white h-11"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#141414] border-[#262626]">
                      <SelectItem value="pending" className="text-white">Pending</SelectItem>
                      <SelectItem value="paid" className="text-white">Paid (already collected)</SelectItem>
                      <SelectItem value="overdue" className="text-white">Overdue</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-white">Deposit ($, optional)</Label>
                  <Input data-testid={`create-${type}-deposit`} type="number" inputMode="decimal" min="0" step="0.01" value={form.deposit_amount} onChange={(e) => setForm({ ...form, deposit_amount: e.target.value })} className="bg-[#0A0A0A] border-[#262626] text-white h-11 text-base" />
                </div>
              </div>
            )}

            {/* Line items — stacked on mobile, grid on sm+ */}
            <div>
              <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                <Label className="text-white">Line Items</Label>
                <div className="flex items-center gap-2 flex-wrap">
                  {/* iter233 — Billing tier drives which catalog price is used. */}
                  <Select value={tier} onValueChange={setTier}>
                    <SelectTrigger data-testid={`create-${type}-tier`} className="bg-[#141414] border-[#262626] text-[#D4AF37] h-8 text-xs w-[150px]"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#141414] border-[#262626]">
                      <SelectItem value="residential" className="text-white">Residential</SelectItem>
                      <SelectItem value="hsp" className="text-white">HSP / Warranty</SelectItem>
                      <SelectItem value="property_management" className="text-white">Property Mgmt</SelectItem>
                      <SelectItem value="commercial" className="text-white">Commercial</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button data-testid={`create-${type}-add-line`} type="button" onClick={addLine} size="sm" variant="outline" className="border-[#262626] text-white hover:bg-[#262626] h-8"><Plus className="w-3 h-3 mr-1" /> Add Custom Service Line</Button>
                  <Button data-testid={`create-${type}-add-membership`} type="button" onClick={() => setShowMembershipPicker(true)} size="sm" variant="outline" className="border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37]/10 h-8"><Plus className="w-3 h-3 mr-1" /> Add Membership Plan</Button>
                </div>
              </div>
              <MembershipPlanPicker
                open={showMembershipPicker}
                onOpenChange={setShowMembershipPicker}
                currentMembership={null}
                onSelect={(line) => setForm((prev) => ({
                  ...prev,
                  line_items: [...prev.line_items, {
                    _key: makeKey(), description: line.description, quantity: 1, unit_price: line.unit_price,
                    kind: 'membership', warranty_covered: false,
                    membership_plan_key: line.membership_plan_key, plan_name: line.plan_name,
                    billing_cadence: line.billing_cadence, complimentary: line.complimentary,
                  }],
                }))}
              />
              <div className="space-y-3">
                {form.line_items.map((li, idx) => (
                  <div key={li._key} data-testid={`create-${type}-line-${idx}`} className="bg-[#0A0A0A] border border-[#262626] rounded-md p-3 space-y-2">
                    {pricebook.length > 0 && (
                      <Select onValueChange={(v) => addFromCatalog(idx, v)}>
                        <SelectTrigger data-testid={`create-${type}-pricebook-${idx}`} className="bg-[#141414] border-[#262626] text-[#D4AF37] h-9 text-xs"><SelectValue placeholder="↳ Add Service From Catalog…" /></SelectTrigger>
                        <SelectContent className="bg-[#141414] border-[#262626] max-h-64">
                          {pricebook.map((p) => {
                            const r = p.resolved || {};
                            const na = r.pricing_method === 'not_available';
                            return (
                              <SelectItem key={p.id} value={p.sku} disabled={na} className={na ? 'text-[#525252]' : 'text-white'}>
                                <span className="font-mono text-xs text-[#A3A3A3] mr-2">{p.sku}</span>{p.name} <span className={na ? 'text-[#525252]' : 'text-[#D4AF37]'}>{r.label}</span>
                              </SelectItem>
                            );
                          })}
                        </SelectContent>
                      </Select>
                    )}
                    {li._catalog && (
                      <div className="flex items-center gap-2 text-[10px]" data-testid={`create-${type}-catalog-badge-${idx}`}>
                        <span className="font-mono text-[#A3A3A3]">{li.sku}</span>
                        <span className="px-1.5 py-0.5 rounded border border-emerald-500/40 bg-emerald-500/10 text-emerald-300">{CATALOG_TIER_LABEL[li.resolved_tier] || li.resolved_tier}</span>
                        {li.estimate_required && <span className="px-1.5 py-0.5 rounded border border-sky-500/40 bg-sky-500/10 text-sky-300">Estimate Required — enter approved price</span>}
                      </div>
                    )}
                    <Input
                      data-testid={`create-${type}-line-desc-${idx}`}
                      value={li.description}
                      onChange={(e) => updateLine(idx, 'description', e.target.value)}
                      placeholder="Description (e.g. Water heater installation)"
                      className="bg-[#141414] border-[#262626] text-white h-11 text-base"
                    />
                    {/* iter150 — Per-line categorization. Default = service.
                        Determines membership-discount eligibility. */}
                    <div className="grid grid-cols-[2fr_auto] gap-2 items-center">
                      <Select value={li.kind || 'service'} onValueChange={(v) => updateLine(idx, 'kind', v)} disabled={li._catalog}>
                        <SelectTrigger data-testid={`create-${type}-line-kind-${idx}`} className="bg-[#141414] border-[#262626] text-white h-9 text-xs">
                          <SelectValue placeholder="Kind" />
                        </SelectTrigger>
                        <SelectContent className="bg-[#141414] border-[#262626]">
                          {LINE_KIND_OPTIONS.map((opt) => (
                            <SelectItem key={opt.value} value={opt.value} className="text-white">
                              <span className="font-bold">{opt.label}</span>
                              <span className="text-[#A3A3A3] text-[10px] ml-2">{opt.hint}</span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <label className="text-[10px] text-[#A3A3A3] flex items-center gap-1.5 cursor-pointer select-none px-2">
                        <input
                          data-testid={`create-${type}-line-warranty-${idx}`}
                          type="checkbox"
                          checked={!!li.warranty_covered}
                          onChange={(e) => updateLine(idx, 'warranty_covered', e.target.checked)}
                          className="w-3 h-3 accent-purple-500"
                        />
                        HSP/Warranty
                      </label>
                    </div>
                    <div className="grid grid-cols-[1fr_1fr_44px] gap-2 items-center">
                      <div>
                        <Label className="text-[10px] uppercase tracking-wider text-[#525252]">Qty</Label>
                        <Input
                          data-testid={`create-${type}-line-qty-${idx}`}
                          type="number"
                          inputMode="decimal"
                          min="0"
                          step="0.5"
                          placeholder="1"
                          // iter129 — Show empty string when 0 so users can type fresh.
                          // onFocus selects the whole field so typing replaces.
                          value={(li.quantity === 0 || li.quantity === '' || li.quantity == null) ? '' : String(li.quantity)}
                          onFocus={(e) => e.target.select()}
                          onChange={(e) => updateLine(idx, 'quantity', e.target.value === '' ? 0 : Number(e.target.value))}
                          className="bg-[#141414] border-[#262626] text-white h-11 text-base text-right"
                        />
                      </div>
                      <div>
                        <Label className="text-[10px] uppercase tracking-wider text-[#525252]">Unit Price</Label>
                        <Input
                          data-testid={`create-${type}-line-price-${idx}`}
                          type="number"
                          inputMode="decimal"
                          min="0"
                          step="0.01"
                          placeholder="0.00"
                          value={(li.unit_price === 0 || li.unit_price === '' || li.unit_price == null) ? '' : String(li.unit_price)}
                          onFocus={(e) => e.target.select()}
                          onChange={(e) => updateLine(idx, 'unit_price', e.target.value === '' ? 0 : Number(e.target.value))}
                          className="bg-[#141414] border-[#262626] text-white h-11 text-base text-right"
                        />
                      </div>
                      <div className="flex flex-col items-end">
                        <Label className="text-[10px] uppercase tracking-wider text-[#525252]">&nbsp;</Label>
                        {form.line_items.length > 1 ? (
                          <Button type="button" onClick={() => removeLine(idx)} size="sm" variant="ghost" className="text-red-400 hover:bg-[#7F1D1D] h-11 w-11 p-0"><Trash2 className="w-4 h-4" /></Button>
                        ) : <span className="h-11" />}
                      </div>
                    </div>
                    <p className="text-right text-[#A3A3A3] text-xs">Line total: <span className="text-[#D4AF37]">${computeLineTotal(li).toFixed(2)}</span></p>
                  </div>
                ))}
              </div>
              <p className="text-right text-[#D4AF37] font-bold text-lg mt-3">Subtotal: ${subtotal.toFixed(2)}</p>
            </div>

            <div>
              <Label className="text-white">Notes</Label>
              <Textarea data-testid={`create-${type}-notes`} rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Terms, warranty info, job notes…" className="bg-[#0A0A0A] border-[#262626] text-white text-base" />
            </div>

            {!jobContext && <div className="bg-[#0A0A0A] border border-[#D4AF37]/30 rounded-md p-3 text-xs text-[#A3A3A3]">
              <span className="text-[#D4AF37] font-bold">→</span> After you create this {isInvoice ? 'invoice' : 'estimate'}, it opens automatically with all actions ready: <span className="text-white">Send by Email</span>, <span className="text-white">Collect Payment Now</span>, <span className="text-white">Copy Payment Link</span>, <span className="text-white">Show QR</span>, <span className="text-white">Mark Paid</span>, <span className="text-white">Edit</span>.
            </div>}

            <div className="flex gap-2 pt-2 pb-1 sticky bottom-0 bg-[#0D0D0D]">
              <Button data-testid={`create-${type}-submit`} type="submit" disabled={loading} className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB] flex-1 h-11">{loading ? 'Creating…' : `Create ${isInvoice ? 'Invoice' : 'Estimate'}`}</Button>
              <Button type="button" onClick={() => setOpen(false)} variant="outline" className="border-[#262626] text-white hover:bg-[#262626] h-11">Cancel</Button>
            </div>
          </form>
        </SheetContent>
      </Sheet>

      <QuickAddCustomerDialog open={quickAddOpen} onOpenChange={setQuickAddOpen} onCreated={onCustomerCreated} />
    </>
  );
};

export default BillingCreateSheet;
